import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ShieldCheck, Upload, RefreshCw, FileImage } from "lucide-react";
import { api, getToken } from "../api/client";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Thumb = ({ id }) => {
    const [src, setSrc] = useState(null);
    useEffect(() => {
        let active = true;
        let blob = null;
        (async () => {
            try {
                const r = await fetch(`${BACKEND_URL}/api/prescriptions/${id}/image`, {
                    headers: { Authorization: `Bearer ${getToken()}` },
                });
                if (!r.ok) return;
                const b = await r.blob();
                blob = URL.createObjectURL(b);
                if (active) setSrc(blob);
            } catch {}
        })();
        return () => {
            active = false;
            if (blob) URL.revokeObjectURL(blob);
        };
    }, [id]);
    if (!src) return <div className="h-16 w-16 shrink-0 skeleton rounded-xl" />;
    return <img src={src} alt="Rx" className="h-16 w-16 shrink-0 rounded-xl object-cover ring-1 ring-zinc-200" />;
};

const PrescriptionPickerSheet = ({ open, onClose, onSelect }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        let active = true;
        (async () => {
            try {
                setLoading(true);
                const { data } = await api.get("/prescriptions");
                if (active) setItems(data);
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [open]);

    if (!open) return null;

    const extracted = items.filter((p) => p.ocr_status === "extracted");
    const processing = items.filter((p) => p.ocr_status === "processing");

    return (
        <div
            className="fixed inset-0 z-[100] flex items-end justify-center bg-zinc-900/40 px-4 backdrop-blur-sm sm:items-center"
            onClick={onClose}
            data-testid="rx-picker-overlay"
        >
            <div
                className="relative w-full max-w-lg overflow-hidden rounded-t-3xl bg-white shadow-brand-lg sm:rounded-3xl"
                onClick={(e) => e.stopPropagation()}
                data-testid="rx-picker"
            >
                <div className="flex items-center justify-between border-b border-zinc-100 px-6 py-5">
                    <div>
                        <p className="label-uppercase">Rx required</p>
                        <h2 className="mt-1 text-lg font-semibold tracking-tight">Attach a prescription</h2>
                    </div>
                    <button onClick={onClose} className="rounded-full p-1.5 text-zinc-500 hover:bg-zinc-100" aria-label="Close" data-testid="rx-picker-close">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="max-h-[60vh] overflow-y-auto p-4">
                    {loading ? (
                        <div className="space-y-3">
                            {Array.from({ length: 2 }).map((_, i) => <div key={i} className="h-20 skeleton rounded-2xl" />)}
                        </div>
                    ) : extracted.length === 0 ? (
                        <div className="rounded-2xl border border-dashed border-zinc-200 bg-zinc-50/60 p-8 text-center" data-testid="rx-picker-empty">
                            <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-white">
                                <FileImage className="h-6 w-6 text-brand-700" />
                            </div>
                            <h3 className="text-base font-medium text-zinc-900">No prescriptions yet</h3>
                            <p className="mt-1 text-sm text-zinc-500">Upload a prescription so our AI can extract the details.</p>
                            <Link to="/prescriptions/upload" className="btn-brand mt-5 inline-flex" data-testid="rx-picker-upload-cta">
                                <Upload className="h-4 w-4" /> Upload prescription
                            </Link>
                            {processing.length > 0 && (
                                <p className="mt-4 inline-flex items-center gap-2 text-xs text-zinc-500">
                                    <RefreshCw className="h-3 w-3 animate-spin" /> {processing.length} still processing…
                                </p>
                            )}
                        </div>
                    ) : (
                        <ul className="space-y-2" data-testid="rx-picker-list">
                            {extracted.map((p) => (
                                <li key={p.id}>
                                    <button
                                        onClick={() => onSelect(p)}
                                        className="flex w-full items-center gap-3 rounded-2xl border border-zinc-100 bg-white p-3 text-left transition-all hover:-translate-y-0.5 hover:border-brand-200 hover:shadow-brand"
                                        data-testid={`picker-rx-${p.id}`}
                                    >
                                        <Thumb id={p.id} />
                                        <div className="min-w-0 flex-1">
                                            <p className="truncate text-sm font-medium text-zinc-900">{p.doctor_name || "Doctor —"}</p>
                                            <p className="truncate text-xs text-zinc-500">{p.patient_name || "Patient —"} • {p.medicines_count} medicine{p.medicines_count === 1 ? "" : "s"}</p>
                                            <p className="text-xs text-zinc-400">{p.prescription_date || new Date(p.uploaded_at).toLocaleDateString()}</p>
                                        </div>
                                        <ShieldCheck className="h-5 w-5 text-brand-700" />
                                    </button>
                                </li>
                            ))}
                            <li className="pt-2">
                                <Link to="/prescriptions/upload" className="flex items-center gap-2 rounded-2xl border border-dashed border-zinc-200 p-3 text-sm font-medium text-brand-700 hover:bg-brand-50/60" data-testid="rx-picker-add-new">
                                    <Upload className="h-4 w-4" /> Upload a new prescription
                                </Link>
                            </li>
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PrescriptionPickerSheet;
